/* One cart controller for the drawer and full cart. Shopify remains the source of totals. */
(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const dialog = document.getElementById('pc-cart-dialog');
  const surfaces = () => [...document.querySelectorAll('[data-cart-surface]')];
  let busy = false;
  let lastFocus;
  let stale = false;
  let pendingCheckout;
  const disabledStates = new Map();

  function feedback(message = '', error = false) {
    document.querySelectorAll(error ? '[data-cart-error]' : '[data-cart-feedback]').forEach(node => {
      node.textContent = message;
      node.hidden = !message;
    });
  }
  function setBusy(value) {
    busy = value;
    surfaces().forEach(surface => surface.setAttribute('aria-busy', String(value)));
    if (value) {
      document.querySelectorAll('[data-cart-surface] button:not([data-cart-checkout]), [data-cart-surface] input, [data-cart-add]').forEach(node => {
        disabledStates.set(node, node.disabled);
        node.disabled = true;
      });
    } else {
      disabledStates.forEach((disabled, node) => { if (node.isConnected) node.disabled = disabled; });
      disabledStates.clear();
      document.querySelectorAll('[data-cart-checkout]').forEach(node => { node.disabled = stale; });
      if (pendingCheckout && !stale) {
        const form = pendingCheckout.querySelector('[data-cart-form]');
        pendingCheckout = null;
        const checkout = form?.querySelector('[data-cart-checkout]');
        if (checkout) form.requestSubmit(checkout);
      }
    }
  }
  async function request(path, body) {
    const response = await fetch(root + path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { method: 'POST', body: JSON.stringify(body) } : {})
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Your bag could not be updated. Please try again.');
    return data;
  }
  function enhance() {
    document.querySelectorAll('[data-native-cart-update]').forEach(node => { node.hidden = true; });
    document.querySelectorAll('[data-cart-step]').forEach(node => { node.hidden = false; });
  }
  async function refresh() {
    // /cart negotiates a cart JSON response for Accept: application/json.
    // Let the Section Rendering API return its HTML fragments instead.
    const response = await fetch(root + 'cart?sections=pc-cro-cart-content', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Your bag could not be refreshed.');
    const sections = await response.json();
    const doc = new DOMParser().parseFromString(sections['pc-cro-cart-content'] || '', 'text/html');
    const content = doc.querySelector('[data-cart-markup]');
    if (!content) throw new Error('Your bag could not be refreshed.');
    surfaces().forEach(surface => { surface.innerHTML = content.outerHTML; });
    const count = Number(content.dataset.itemCount) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count; });
    document.querySelectorAll('[data-cart-open]').forEach(node => { node.setAttribute('aria-label', `View bag, ${count} items`); });
    stale = false;
    enhance();
  }
  async function open({ added = false, returnFocus } = {}) {
    if (!dialog?.showModal) { location.assign(root + 'cart'); return; }
    if (busy) return;
    lastFocus = returnFocus || document.activeElement;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('pc-dialog-open');
    feedback(added ? 'Added to your bag.' : 'Updating your bag…');
    feedback('', true);
    setBusy(true);
    try { await refresh(); feedback(added ? 'Added to your bag.' : ''); }
    catch (error) { stale = true; pendingCheckout = null; feedback('', false); feedback(error.message + ' Open the full bag to review your order.', true); }
    finally { setBusy(false); }
  }
  function close() { dialog?.close(); }
  dialog?.addEventListener('close', () => {
    document.documentElement.classList.remove('pc-dialog-open');
    if (lastFocus?.isConnected) lastFocus.focus();
  });
  dialog?.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  async function change(line, quantity, focusSelector) {
    if (busy) return;
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > 99) {
      feedback('Enter a whole-number quantity from 0 to 99.', true);
      return;
    }
    const surface = line.closest('[data-cart-surface]');
    const key = line.dataset.key;
    const index = [...surface.querySelectorAll('[data-key]')].indexOf(line);
    setBusy(true); feedback('', true); feedback('Updating your bag…');
    try {
      await request('cart/change.js', { id: key, quantity });
      await refresh();
      feedback(quantity ? 'Quantity updated.' : 'Item removed.');
      const lines = [...surface.querySelectorAll('[data-key]')];
      const next = lines.find(node => node.dataset.key === key) || lines[Math.min(index, lines.length - 1)];
      const target = next?.querySelector(focusSelector) || surface.querySelector('a, button');
      target?.focus();
    } catch (error) {
      pendingCheckout = null;
      stale = true;
      try { await refresh(); } catch (_) { /* Keep checkout disabled until the cart is confirmed. */ }
      feedback('', false);
      feedback(error.message + (stale ? ' Open the full bag to confirm your order before checkout.' : ''), true);
    }
    finally { setBusy(false); }
  }
  document.addEventListener('click', event => {
    const target = event.target.closest('[data-cart-open], [data-cart-navigation], [data-cart-close], [data-cart-step], [data-cart-remove]');
    if (!target) return;
    if (target.hasAttribute('data-cart-navigation')) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); event.stopImmediatePropagation(); location.assign(target.href); return;
    }
    if (target.hasAttribute('data-cart-open')) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); event.stopImmediatePropagation(); open(); return;
    }
    if (target.hasAttribute('data-cart-close')) { event.preventDefault(); close(); return; }
    event.preventDefault();
    const line = target.closest('[data-key]');
    if (!line || busy) return;
    if (target.hasAttribute('data-cart-remove')) { change(line, 0, '[data-cart-remove]'); return; }
    const input = line.querySelector('[data-cart-quantity]');
    const quantity = Math.max(0, Number(input.value) + Number(target.dataset.cartStep));
    change(line, quantity, `[data-cart-step="${target.dataset.cartStep}"]`);
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-cart-quantity]')) return;
    const input = event.target;
    if (input.value === '') { feedback('Enter a quantity before continuing.', true); return; }
    change(input.closest('[data-key]'), Number(input.value), '[data-cart-quantity]');
  });
  document.addEventListener('submit', async event => {
    if (event.target.matches('[data-cart-extra-form]')) {
      event.preventDefault();
      if (busy) return;
      const form = event.target;
      setBusy(true); feedback('', true); feedback('Adding treats…');
      let added = false;
      try {
        await request('cart/add.js', { items: [{ id: Number(form.elements.namedItem('id').value), quantity: 1 }] });
        added = true;
        await refresh();
        feedback('Treats added as a one-time purchase.');
      } catch (error) {
        pendingCheckout = null; stale = true;
        try { await refresh(); } catch (_) { /* The full cart link remains available. */ }
        feedback('', false); feedback(added ? 'Treats were added. Review your bag before adding again.' : error.message, true);
      }
      finally { setBusy(false); }
      return;
    }
    if (!event.target.matches('[data-cart-form]')) return;
    if (busy) {
      event.preventDefault();
      if (event.submitter?.hasAttribute('data-cart-checkout')) {
        pendingCheckout = event.target.closest('[data-cart-surface]');
        feedback('Updating your bag before checkout…');
      }
      return;
    }
    if (stale) { event.preventDefault(); feedback('Open the full bag to confirm your order before checkout.', true); return; }
    // Preserve Shopify's native form submission, including quantities and checkout button.
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Tab' && dialog?.open) {
      const focusable = [...dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]')].filter(node => !node.disabled && node.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    }
    if (event.key === 'Enter' && event.target.matches('[data-cart-quantity]')) {
      event.preventDefault();
      event.target.blur();
    }
  });
  window.PCCart = { open, close, refresh };
  enhance();
})();
